import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PageMeta } from '@aurora/contracts';
import { relativeTime } from '../../lib/format';
import { Button, Select } from '../../components/ui';
import { useNow } from './sla-now';

/**
 * The foot of a ticket list: where you are, how fresh it is, and the way on.
 *
 * The range is only printed for a page that has rows. A page emptied from
 * under the reader — the last few tickets on it resolved in bulk — used to read
 * "121–91 of 91" and "Page 5 of 4" while the list above announced that nothing
 * matched; the list now steps back a page instead (see the route).
 */

export const PER_PAGE_CHOICES = [30, 50, 100] as const;

export function ListPagination({
  meta,
  shown,
  onPage,
  perPage,
  onPerPage,
  updatedAt,
  order,
}: {
  meta: PageMeta;
  /** Rows actually on screen. */
  shown: number;
  onPage: (page: number) => void;
  perPage: number;
  onPerPage?: (perPage: number) => void;
  /** When the rows were fetched, so "is this current?" has an answer. */
  updatedAt?: number;
  /** "Sorted by …", so the order is never a mystery. */
  order?: string;
}) {
  useNow();
  const first = (meta.page - 1) * meta.per_page + 1;
  const last = first + shown - 1;

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line bg-surface px-4 py-2 sm:px-6">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-mini text-ink-muted">
        {shown > 0 && (
          <span className="tabular-nums">
            {first.toLocaleString()}–{last.toLocaleString()} of {meta.total.toLocaleString()}
          </span>
        )}
        {order && <span className="truncate">Sorted by {order.charAt(0).toLowerCase() + order.slice(1)}</span>}
        {updatedAt ? <span className="text-ink-subtle">Updated {relativeTime(new Date(updatedAt))}</span> : null}
      </div>
      <div className="flex items-center gap-1">
        {onPerPage && (
          <label className="me-2 flex items-center gap-1.5 text-mini text-ink-muted">
            Per page
            <Select
              size="sm"
              value={String(perPage)}
              onChange={(event) => onPerPage(Number(event.target.value))}
              className="w-auto pe-6"
            >
              {PER_PAGE_CHOICES.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </Select>
          </label>
        )}
        <Button size="sm" variant="ghost" disabled={!meta.has_prev} onClick={() => onPage(meta.page - 1)}>
          <ChevronLeft aria-hidden className="size-3.5 rtl:rotate-180" />
          Previous
        </Button>
        {meta.total_pages > 1 && (
          <span className="px-2 text-mini text-ink-muted tabular-nums">
            Page {Math.min(meta.page, meta.total_pages)} of {meta.total_pages}
          </span>
        )}
        <Button size="sm" variant="ghost" disabled={!meta.has_next} onClick={() => onPage(meta.page + 1)}>
          Next
          <ChevronRight aria-hidden className="size-3.5 rtl:rotate-180" />
        </Button>
      </div>
    </div>
  );
}
